import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { AppRecord } from '../../lib/types';
import { buildCompassHref, labelStatusToken, type CompassCopy, type CompassLocale } from '../i18n';
import { useRuntimeStatus } from '../hooks/useRuntimeStatus';
import { deriveAppCategory, deriveAppTags, promptExcerpt, relatedApps, resolveDemoHref } from '../lib/catalogPresentation';

function appHighlights(app: AppRecord, locale: CompassLocale): string[] {
  const highlights: string[] = [];
  if (app.deploy.present) {
    highlights.push(locale === 'zh-CN' ? '包含部署包与启动脚本，可继续进入运行态验证。' : 'Includes deploy artifacts and a start script for runtime validation.');
  }
  if (app.axle.resources.length > 0) {
    highlights.push(locale === 'zh-CN' ? `暴露 ${app.axle.resources.length} 个 API 资源，可直接对照接口设计。` : `Exposes ${app.axle.resources.length} API resources for interface review.`);
  }
  if (app.screenshots.length > 0) {
    highlights.push(locale === 'zh-CN' ? `保留 ${app.screenshots.length} 张视觉证据，可回看结果质量。` : `Keeps ${app.screenshots.length} visual references for output review.`);
  }
  if (app.statuses.harnessScore !== undefined) {
    highlights.push(locale === 'zh-CN' ? `Harness 分数 ${app.statuses.harnessScore}，可快速判断成品完成度。` : `Harness score ${app.statuses.harnessScore} gives a fast quality signal.`);
  }
  return highlights.slice(0, 4);
}

export function Detail(props: {
  app: AppRecord;
  apps: AppRecord[];
  locale: CompassLocale;
  copy: CompassCopy;
  onLocaleChange: (locale: CompassLocale) => void;
  isIntegrated?: boolean;
}): React.JSX.Element {
  const { app, apps, locale, copy } = props;
  const { runtimeStatus, starting, startApp, stopApp } = useRuntimeStatus(app.appId);
  const [showTranslation, setShowTranslation] = useState(false);
  const hasTranslation = Boolean(app.promptTranslatedZh);
  const displayPrompt = showTranslation ? (app.promptTranslatedZh ?? app.promptText) : app.promptText;

  const previewHref = resolveDemoHref(app, runtimeStatus) ?? `/runtime/apps/${app.appId}/`;
  const canOpenPreview = Boolean(resolveDemoHref(app, runtimeStatus));
  const similarApps = relatedApps(apps, app.appId, 3);
  const tags = deriveAppTags(app);
  const highlights = appHighlights(app, locale);

  const headerText = locale === 'zh-CN'
    ? {
        back: '返回列表',
        copyPrompt: '复制 Prompt',
        openDemo: canOpenPreview ? '打开 Demo' : '启动 Demo',
        overview: 'Overview & Use Cases',
        analysis: 'Result Analysis',
        prompt: 'Prompt Blueprint',
        similar: 'Similar Architectures',
        quality: '质量信号',
        deploy: '部署状态',
        endpoints: 'Endpoint Inventory',
        viewFull: '查看完整分辨率',
        stop: '停止实例',
        previewEmpty: '当前没有可用预览，可先进入运行态或查看下方证据。',
      }
    : {
        back: 'Back to List',
        copyPrompt: 'Copy Prompt',
        openDemo: canOpenPreview ? 'Open Demo' : 'Start Demo',
        overview: 'Overview & Use Cases',
        analysis: 'Result Analysis',
        prompt: 'Prompt Blueprint',
        similar: 'Similar Architectures',
        quality: 'Quality Signals',
        deploy: 'Deploy Status',
        endpoints: 'Endpoint Inventory',
        viewFull: 'View Full Resolution',
        stop: 'Stop Instance',
        previewEmpty: 'No preview is currently available. Start the runtime or inspect the evidence below.',
      };

  return (
    <main className="flex-grow pt-[100px] pb-24 px-4 md:px-10 max-w-[1280px] mx-auto w-full">
      <header className="mb-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="max-w-2xl">
          <a className="inline-flex items-center gap-2 text-primary hover:text-white mb-4 transition-colors no-underline" href={buildCompassHref('/', locale)}>
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            <span>{headerText.back}</span>
          </a>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <h1 className="font-geist text-[48px] leading-[1.1] font-bold text-on-surface">{app.name}</h1>
            <span className="bg-surface-container-high text-on-surface-variant px-3 py-1 rounded-full text-xs border border-outline-variant/30">
              {app.appId}
            </span>
          </div>
          <p className="text-lg leading-relaxed text-on-surface-variant mb-6">{promptExcerpt(app.summary, 320)}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {tags.map((tag) => (
              <span key={tag} className="bg-surface-container-lowest border border-outline-variant/50 text-on-surface px-3 py-1 rounded-full text-xs">
                {tag}
              </span>
            ))}
          </div>
          <div className="text-xs uppercase tracking-widest text-outline-variant">
            {copy.pipeline}: {labelStatusToken(app.statuses.pipelineStatus, locale)}
          </div>
        </div>

        <div className="flex gap-4 shrink-0 w-full md:w-auto">
          <button
            className="btn-secondary-cyan flex-1 md:flex-none px-6 py-3 rounded-full text-sm flex items-center justify-center gap-2"
            onClick={() => navigator.clipboard.writeText(app.promptText)}
            type="button"
          >
            <span className="material-symbols-outlined text-[18px]">content_copy</span>
            {headerText.copyPrompt}
          </button>
          <button
            className="btn-primary-gradient flex-1 md:flex-none px-6 py-3 rounded-full text-sm text-white flex items-center justify-center gap-2"
            disabled={starting}
            onClick={() => {
              if (canOpenPreview) {
                window.open(previewHref, '_blank', 'noopener,noreferrer');
                return;
              }
              startApp();
            }}
            type="button"
          >
            <span className="material-symbols-outlined text-[18px]">{canOpenPreview ? 'play_arrow' : 'rocket_launch'}</span>
            {headerText.openDemo}
          </button>
        </div>
      </header>

      <section className="mb-16">
        <div className="glass-panel-l2 rounded-[24px] overflow-hidden aspect-video relative group flex items-center justify-center bg-surface-container-low">
          <div className="absolute inset-0 bg-surface-container/50" />
          {runtimeStatus === 'healthy' ? (
            <iframe className="w-full h-full border-none relative z-10" src={`/runtime/apps/${app.appId}/`} title={copy.liveFrameTitle(app.name)} />
          ) : app.preview.url ? (
            <iframe className="w-full h-full border-none relative z-10" src={previewHref} title={copy.previewFrameTitle(app.name)} />
          ) : app.screenshots[0]?.publishedUrl ? (
            <img alt={app.name} className="w-full h-full object-cover mix-blend-luminosity opacity-50 group-hover:opacity-70 transition-opacity duration-500 relative z-10" src={app.screenshots[0].publishedUrl} />
          ) : (
            <div className="relative z-10 flex flex-col items-center gap-4">
              <span className="material-symbols-outlined text-6xl text-outline-variant">play_circle</span>
              <p className="text-on-surface-variant">{headerText.previewEmpty}</p>
            </div>
          )}
          {runtimeStatus === 'healthy' && (
            <button
              className="absolute right-6 top-6 z-20 bg-black/40 border border-red-400/30 text-red-300 rounded-full px-4 py-2 text-xs uppercase tracking-widest"
              onClick={stopApp}
              type="button"
            >
              {headerText.stop}
            </button>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        <div className="lg:col-span-2 flex flex-col gap-12">
          <section>
            <h2 className="font-geist text-[32px] leading-[1.2] font-semibold text-on-surface mb-4">{headerText.overview}</h2>
            <div className="glass-card rounded-2xl p-6 space-y-4 text-on-surface-variant">
              <div className="prose-refined max-w-none">
                <ReactMarkdown>{app.scribeContent ?? app.summary}</ReactMarkdown>
              </div>
              <ul className="list-disc pl-5 space-y-2 text-on-surface">
                {highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          <section>
            <h2 className="font-geist text-[32px] leading-[1.2] font-semibold text-on-surface mb-4">{headerText.analysis}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass-card rounded-2xl p-6 border-l-4 border-l-cyan">
                <h3 className="text-lg font-semibold text-on-surface mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-cyan">check_circle</span>
                  {headerText.quality}
                </h3>
                <div className="space-y-3 text-sm text-on-surface-variant">
                  <p>{copy.qaStatus}: {app.statuses.accepted ? copy.yes : copy.no}</p>
                  <p>{copy.pipeline}: {labelStatusToken(app.statuses.pipelineStatus, locale)}</p>
                  <p>{copy.scoreLabel(app.statuses.harnessScore ?? 0)}</p>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6 border-l-4 border-l-primary">
                <h3 className="text-lg font-semibold text-on-surface mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">deployed_code</span>
                  {headerText.deploy}
                </h3>
                <div className="space-y-3 text-sm text-on-surface-variant">
                  <p>{copy.deployPackage}: {app.deploy.present ? copy.yes : copy.no}</p>
                  <p>{copy.runtimeSmoke}: {app.deploy.smokeOk ? copy.smokePassed : copy.smokeStatusUnknown}</p>
                  {app.deploy.startScript && <p className="font-mono break-all text-primary">{app.deploy.startScript}</p>}
                </div>
              </div>
            </div>
          </section>

          {app.axle.resources.length > 0 && (
            <section>
              <h2 className="font-geist text-[32px] leading-[1.2] font-semibold text-on-surface mb-4">{headerText.endpoints}</h2>
              <div className="glass-card rounded-2xl overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-white/5">
                      <th className="p-5 text-[10px] uppercase tracking-[0.2em] text-outline">{copy.resource}</th>
                      <th className="p-5 text-[10px] uppercase tracking-[0.2em] text-outline">{copy.path}</th>
                      <th className="p-5 text-[10px] uppercase tracking-[0.2em] text-outline">{copy.operations}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {app.axle.resources.map((resource, index) => (
                      <tr key={resource.name} className={index % 2 === 0 ? 'border-t border-outline-variant/10' : 'border-t border-outline-variant/10 bg-white/[0.02]'}>
                        <td className="p-5 text-cyan font-semibold">{resource.name}</td>
                        <td className="p-5 font-mono text-xs text-on-surface-variant">{resource.path}</td>
                        <td className="p-5">
                          <div className="flex flex-wrap gap-2">
                            {resource.operations.map((operation) => (
                              <span key={operation} className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-[10px] uppercase tracking-widest text-on-surface">
                                {operation}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section>
            <h2 className="font-geist text-[32px] leading-[1.2] font-semibold text-on-surface mb-4">{copy.artifacts}</h2>
            <div className="glass-card rounded-2xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {app.artifacts.map((artifact) => (
                  <div
                    key={artifact.key}
                    className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-4 py-4 flex items-center justify-between gap-4"
                  >
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="text-sm font-semibold text-on-surface">{artifact.label}</span>
                      <span className="font-mono text-[11px] text-outline-variant truncate">{artifact.key}</span>
                    </div>
                    {artifact.exists && artifact.publishedUrl ? (
                      <a
                        className="btn-secondary-cyan px-4 py-2 rounded-full text-[11px] uppercase tracking-widest no-underline shrink-0"
                        href={artifact.publishedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {copy.open}
                      </a>
                    ) : (
                      <span className="text-[11px] uppercase tracking-widest text-outline-variant shrink-0">{copy.missing}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {similarApps.length > 0 && (
            <section className="mt-8 border-t border-outline-variant/10 pt-12">
              <h2 className="font-geist text-[32px] leading-[1.2] font-semibold text-on-surface mb-8">{headerText.similar}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarApps.map((related) => (
                  <a
                    key={related.appId}
                    className="glass-card rounded-2xl overflow-hidden group cursor-pointer flex flex-col no-underline"
                    href={buildCompassHref(`/apps/${related.appId}`, locale)}
                  >
                    <div className="h-40 bg-surface-container relative overflow-hidden">
                      {related.screenshots[0]?.publishedUrl ? (
                        <img alt={related.name} className="w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity duration-300" src={related.screenshots[0].publishedUrl} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-outline-variant">
                          <span className="material-symbols-outlined text-5xl">dashboard</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <span className="text-cyan text-xs uppercase tracking-widest mb-2">{deriveAppCategory(related)}</span>
                      <h3 className="text-lg font-semibold text-on-surface mb-2">{related.name}</h3>
                      <p className="text-sm text-on-surface-variant mb-4 line-clamp-2">{related.summary}</p>
                      <div className="mt-auto flex justify-between items-center">
                        <div className="text-xs text-outline">{related.appId}</div>
                        <span className="material-symbols-outlined text-primary group-hover:translate-x-1 transition-transform">arrow_forward</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="flex flex-col gap-8">
            <div className="glass-panel-l2 rounded-2xl flex flex-col relative overflow-hidden">
              <div className="p-4 border-b border-outline-variant/30 flex justify-between items-center bg-surface-container-highest/50">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs uppercase tracking-widest text-cyan">{headerText.prompt}</span>
                  {hasTranslation && (
                    <div className="flex items-center gap-0.5 p-0.5 bg-white/5 border border-white/10 rounded-full">
                      <button
                        className={`px-3 py-1 rounded-full font-geist font-bold text-[10px] uppercase tracking-widest border-none cursor-pointer transition-all ${!showTranslation ? 'bg-[#00ffff] text-[#030712]' : 'bg-transparent text-on-surface-variant hover:text-[#00ffff]'}`}
                        onClick={() => setShowTranslation(false)}
                        type="button"
                      >
                        EN
                      </button>
                      <button
                        className={`px-3 py-1 rounded-full font-geist font-bold text-[10px] uppercase tracking-widest border-none cursor-pointer transition-all ${showTranslation ? 'bg-[#00ffff] text-[#030712]' : 'bg-transparent text-on-surface-variant hover:text-[#00ffff]'}`}
                        onClick={() => setShowTranslation(true)}
                        type="button"
                      >
                        中文
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className="text-outline hover:text-primary transition-colors flex items-center gap-1 text-xs"
            onClick={() => navigator.clipboard.writeText(displayPrompt)}
                  type="button"
                >
                  <span className="material-symbols-outlined text-sm">content_copy</span>
                  Copy
                </button>
              </div>
              <div className="p-6 max-h-[70vh] overflow-y-auto font-mono text-sm text-on-surface-variant leading-relaxed bg-surface-container-lowest">
                <pre className="whitespace-pre-wrap break-words m-0">{displayPrompt}</pre>
              </div>
            </div>

            {app.screenshots.length > 0 && (
              <div className="flex flex-col gap-4">
                {app.screenshots.slice(0, 2).map((shot) => (
                  <div key={shot.label} className="glass-card rounded-2xl overflow-hidden">
                    <div className="aspect-[16/10] bg-surface-container relative">
                      {shot.publishedUrl ? (
                        <img alt={shot.label} className="w-full h-full object-cover opacity-60" src={shot.publishedUrl} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-outline-variant">
                          <span className="material-symbols-outlined text-5xl">image</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex justify-between items-center bg-white/5">
                      <span className="text-sm text-on-surface">{shot.label}</span>
                      <span className="text-[10px] uppercase tracking-widest text-outline-variant">{headerText.viewFull}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}
